import { useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import { useMemo, type JSX } from "react";
import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerRoot,
  DrawerTitle,
} from "@/components/ui/drawer/drawer";
import { Button } from "@/components/ui/button/button";
import { trackEvent } from "@/lib/tracking";
import {
  ColoredCardInfo,
  ColoredCardInfoDescription,
  ColoredCardInfoHeader,
  ColoredCardInfoTitle,
} from "@/components/ui/card/colored-card-info";
import { formatCurrencyFromCentsToReais } from "@/lib/helpers/currency";
import type { InfoVariantBannerReferral } from "@/lib/launchdarkly";
import { processMarkdownText } from "@/utils/process-markdown-text";

interface ReferralBannerDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const ReferralBannerDrawer = ({
  open,
  setOpen,
}: ReferralBannerDrawerProps): JSX.Element => {
  const router = useRouter();
  const { flags } = useRouteContext({ from: "__root__" });
  const searchParams: Record<string, unknown> = useSearch({ strict: false });
  const referralValue = useMemo(
    () => flags["valor-do-gentileza-gera-gentileza"],
    [flags],
  );

  const formattedReferralValue = useMemo(
    () => formatCurrencyFromCentsToReais(referralValue * 100),
    [referralValue],
  );

  const handleReferralClick = (): void => {
    trackEvent("Banner CSAT Clicado", {
      secao: "Gentileza Gera Gentileza",
      CTA: "Indique e ganhe",
    });

    void router.navigate({
      to: "/settings/referral",
      search: { ...searchParams, action: undefined },
      replace: true,
    });
  };

  const infoJson = useMemo<InfoVariantBannerReferral>(
    () => flags["gerenciar-titulo-e-descricao-do-gentileza-gera-gentileza"],
    [flags],
  );

  const processedDescription = useMemo<React.ReactNode[]>(() => {
    return processMarkdownText(
      infoJson.homeBanner?.infoDescription ??
        "Ganhe **{{value}}** em créditos para cada colega que **abrir o consultório próprio** na Livance.",
      {
        value: formattedReferralValue,
      },
    );
  }, [infoJson.homeBanner?.infoDescription, formattedReferralValue]);

  return (
    <DrawerRoot open={open} setOpen={setOpen}>
      <DrawerContent className="flex flex-col min-h-[300px] max-h-[90vh] h-auto overflow-hidden">
        <DrawerTitle className="text-xl font-medium text-neutral-700 text-center mt-2">
          Obrigado pelo seu feedback
        </DrawerTitle>

        <DrawerBody>
          <p className="text-center text-md text-neutral-500">
            Aproveite e indique a Livance para que seus colegas possam ter essas boas
            experiências também! <b>Todos ganham com boas indicações</b>
          </p>
        </DrawerBody>
        <ColoredCardInfo className="bg-white">
          <ColoredCardInfoHeader className="text-xl">
            <ColoredCardInfoTitle className="text-secondary-500">
              {infoJson.homeBanner?.infoTitle ?? "Indique e ganhe"}
            </ColoredCardInfoTitle>
            <ColoredCardInfoDescription className="text-md text-secondary-500">
              {processedDescription}
            </ColoredCardInfoDescription>
          </ColoredCardInfoHeader>
        </ColoredCardInfo>

        <DrawerFooter>
          <Button fullWidth onClick={handleReferralClick} type="button">
            Indicar a Livance
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </DrawerRoot>
  );
};
